import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import React, { useEffect, useRef } from "react";
import useLang from "Shared/hooks/useLanguage";

export interface SensitivityRow {
  id: number;
  testConfigId?: number;
  antimicrobial: string;
  mic: string;
  interpretation: string;
  isSelected: boolean;
}

interface SensitivityDataTableProps {
  rows: SensitivityRow[];
  onRowChange: (rowId: number, field: keyof SensitivityRow, value: any) => void;
  onSelectAll?: (isSelected: boolean) => void;
  showValidation?: boolean;
}

const SensitivityDataTable: React.FC<SensitivityDataTableProps> = ({
  rows,
  onRowChange,
  onSelectAll,
  showValidation = false,
}) => {
  const { t } = useLang();
  const selectAllRef = useRef<HTMLInputElement>(null);

  const allSelected = rows.length > 0 && rows.every((row) => row.isSelected);
  const someSelected = rows.some((row) => row.isSelected);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [allSelected, someSelected]);

  if (!rows || rows.length === 0) {
    return null;
  }

  const getInputClass = (row: SensitivityRow, field: "mic" | "interpretation") => {
    const base = "form-control bg-white rounded-2 h-30px fs-7 w-100";
    const value = field === "mic" ? row.mic : row.interpretation;
    const isInvalid = showValidation && row.isSelected && !String(value ?? "").trim();
    return isInvalid ? `${base} is-invalid` : base;
  };

  const isFieldInvalid = (row: SensitivityRow, field: "mic" | "interpretation") => {
    const value = field === "mic" ? row.mic : row.interpretation;
    return showValidation && row.isSelected && !String(value ?? "").trim();
  };

  return (
    <div className="table_bordered overflow-hidden">
      <TableContainer component={Paper} className="shadow-none">
        <Table
          className="table table-bordered table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
          aria-label="sensitivity data table"
        >
          <TableHead>
            <TableRow className="h-30px">
              <TableCell className="fw-bold" sx={{ width: "49px" }}>
                <label className="form-check form-check-sm form-check-solid d-flex justify-content-center m-0">
                  <input
                    ref={selectAllRef}
                    id="SensitivityAntimicrobialCheckBox_SelectAll"
                    className="form-check-input"
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => onSelectAll?.(e.target.checked)}
                    aria-label={t("Select all tests")}
                  />
                </label>
              </TableCell>
              <TableCell className="fw-bold" sx={{ width: "35%" }}>
                {t("Antimicrobial")}
              </TableCell>
              <TableCell className="fw-bold" sx={{ width: "30%" }}>
                {t("MIC")}
              </TableCell>
              <TableCell className="fw-bold" sx={{ width: "30%" }}>
                {t("Interpretation")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} className="h-30px">
                <TableCell sx={{ width: "49px" }}>
                  <label className="form-check form-check-sm form-check-solid d-flex justify-content-center m-0">
                    <input
                      id={`SensitivityAntimicrobialCheckBox_${row.id}`}
                      className="form-check-input"
                      type="checkbox"
                      checked={row.isSelected}
                      onChange={(e) => onRowChange(row.id, "isSelected", e.target.checked)}
                    />
                  </label>
                </TableCell>
                <TableCell className="h-30px">{row.antimicrobial}</TableCell>
                <TableCell className="h-30px">
                  <input
                    id={`SensitivityMicInput_${row.id}`}
                    type="text"
                    autoComplete="off"
                    className={getInputClass(row, "mic")}
                    value={row.mic ?? ""}
                    required={row.isSelected}
                    aria-invalid={isFieldInvalid(row, "mic")}
                    placeholder={row.isSelected ? t("Required") : ""}
                    onChange={(e) => onRowChange(row.id, "mic", e.target.value)}
                  />
                </TableCell>
                <TableCell className="h-30px">
                  <input
                    id={`SensitivityInterpretationInput_${row.id}`}
                    type="text"
                    autoComplete="off"
                    className={getInputClass(row, "interpretation")}
                    value={row.interpretation ?? ""}
                    required={row.isSelected}
                    aria-invalid={isFieldInvalid(row, "interpretation")}
                    placeholder={row.isSelected ? t("Required") : ""}
                    onChange={(e) => onRowChange(row.id, "interpretation", e.target.value)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default SensitivityDataTable;
